import { useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { addIconGlassLg } from '@tpct/icons';
import { __ } from '../i18n/vsme';

/** One file per pick (a mission document), or every file picked at once (`multiple`). */
type FileSelection =
  | { multiple?: false; onFileSelect: (file: File) => void; onFilesSelect?: never }
  | { multiple: true; onFilesSelect: (files: File[]) => void; onFileSelect?: never };

type MissionDocumentDropzoneProps = FileSelection & {
  inputId: string;
  accept: string;
  ariaLabel: string;
  primaryLabel: ReactNode;
  disabled?: boolean;
  busy?: boolean;
  /** Sizes the dropzone in its consumer's layout (a BEM element of the consumer's block). */
  className?: string;
  /** A click calls this instead of opening the file dialog (the consumer has its own picker); a drop still selects. */
  onActivate?: () => void;
};

/** The dossier form's file dropzone (`ProfileArtifactFileDropzone`), shared by the mission screens. */
const MissionDocumentDropzone = ({
  inputId,
  accept,
  ariaLabel,
  primaryLabel,
  disabled = false,
  busy = false,
  className,
  onActivate,
  ...selection
}: MissionDocumentDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const isDisabled = disabled || busy;

  const selectFiles = (files: File[]) => {
    const [first] = files;
    if (first === undefined || isDisabled) {
      return;
    }
    if (selection.multiple === true) {
      selection.onFilesSelect(files);
      return;
    }
    selection.onFileSelect(first);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    selectFiles(files);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    selectFiles(Array.from(event.dataTransfer.files));
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!isDisabled) {
      setDragging(true);
    }
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  return (
    <div className={className === undefined ? 'company-form__import-actions' : `company-form__import-actions ${className}`}>
      {onActivate === undefined ? (
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="visually-hidden"
          accept={accept}
          multiple={selection.multiple === true}
          disabled={isDisabled}
          aria-label={ariaLabel}
          onChange={handleInputChange}
        />
      ) : null}
      <button
        type="button"
        className={`dropzone${dragging ? ' dragging' : ''}${isDisabled ? ' disabled' : ''}`}
        disabled={isDisabled}
        onClick={() => (onActivate === undefined ? inputRef.current?.click() : onActivate())}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="dropzone-icon" aria-hidden="true">
          <img src={addIconGlassLg} alt="" />
        </div>
        <div className="dropzone-text">
          <span className="dropzone-primary">{busy ? __('Dépôt en cours...') : primaryLabel}</span>
        </div>
      </button>
    </div>
  );
};

export default MissionDocumentDropzone;
